import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { CheckCircle, XCircle, Clock, FileText, ExternalLink, ShieldCheck, MessageSquare, Eye, X, Download } from 'lucide-react'
import { format } from 'date-fns'
import { AlertCircle } from 'lucide-react'

function FilePreviewModal({ doc, onClose }) {
  const isImage = doc.content_type?.startsWith('image/')
  const isPdf = doc.content_type === 'application/pdf'
  const hasData = !!doc.file_data
  const [blobUrl, setBlobUrl] = useState(null)

  useEffect(() => {
    let url;
    if (isPdf && hasData && doc.file_data) {
      try {
        const b64Data = doc.file_data.replace(/\s/g, '');
        const byteCharacters = atob(b64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }
        const blob = new Blob(byteArrays, { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        console.error("Failed to create blob URL for PDF", e);
      }
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.file_data, isPdf, hasData]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.file_url || `${doc.title}.pdf`;
      a.click();
    } else if (hasData) {
      const a = document.createElement('a');
      a.href = `data:${doc.content_type};base64,${doc.file_data}`;
      a.download = doc.file_url || `${doc.title}`;
      a.click();
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 1000, width: '95%', height: '90vh', background: 'var(--bg-elevated)', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={20} color="var(--accent)" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h2 style={{ fontSize: '1rem' }}>{doc.title}</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>v{doc.version} • {doc.category} Viewer</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--accent)' }} onClick={handleDownload} title="Download File"><Download size={16} /></button>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: 0, background: '#525659' }}>
          {hasData ? (
            isPdf ? (
              blobUrl ? (
                <iframe 
                  src={`${blobUrl}#view=FitH`}
                  style={{ width: '100%', height: '100%', border: 'none' }} 
                  title={doc.title}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
                  Loading PDF...
                </div>
              )
            ) : isImage ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 20 }}>
                <img src={`data:${doc.content_type};base64,${doc.file_data}`} alt={doc.title} style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }} />
              </div>
            ) : (
              <div style={{ padding: 40, color: 'white', textAlign: 'center' }}>
                <AlertCircle size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                <p>Preview not available for this file type ({doc.content_type}).</p>
                <p style={{ fontSize: '0.875rem', marginTop: 8, opacity: 0.7 }}>Please download the file to view its content.</p>
              </div>
            )
          ) : (
            <div style={{ padding: 40, color: 'white', textAlign: 'center' }}>
              <Clock size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <p>No actual file content found for this record.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ReviewActionModal({ doc, onClose, onReview, isReviewing }) {
  const [comment, setComment] = useState('')
  const [decision, setDecision] = useState(null) // 'Approved', 'Rejected', 'Needs Improvement'

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={20} color="var(--accent)" />
            <h2>Review Decision</h2>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-elevated)', padding: 16, borderRadius: 12, marginBottom: 20 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Document:</span>
              <span className="badge badge-info">Under Review</span>
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{doc.title}</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              Submitted by <strong>{doc.submitter_name || 'Admin'}</strong> on {doc.submitted_at ? format(new Date(doc.submitted_at), 'MMM dd, yyyy') : 'Recently'}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Review Feedback / Comments *</label>
            <textarea
              className="form-input"
              style={{ minHeight: 120, fontSize: '0.875rem' }}
              placeholder="Detail your findings, required changes, or approval reasoning..."
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 24 }}>
            <button
              className={`btn ${decision === 'Approved' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ flexDirection: 'column', height: 'auto', padding: '16px 8px', gap: 8, border: decision === 'Approved' ? 'none' : '1px solid var(--border)' }}
              onClick={() => setDecision('Approved')}
            >
              <CheckCircle size={24} color={decision === 'Approved' ? '#fff' : '#10b981'} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Approve</span>
            </button>

            <button
              className={`btn ${decision === 'Needs Improvement' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ flexDirection: 'column', height: 'auto', padding: '16px 8px', gap: 8, border: decision === 'Needs Improvement' ? 'none' : '1px solid var(--border)' }}
              onClick={() => setDecision('Needs Improvement')}
            >
              <Clock size={24} color={decision === 'Needs Improvement' ? '#fff' : 'var(--warning)'} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, textAlign: 'center' }}>Improvement/changes needed</span>
            </button>

            <button
              className={`btn ${decision === 'Rejected' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ flexDirection: 'column', height: 'auto', padding: '16px 8px', gap: 8, border: decision === 'Rejected' ? 'none' : '1px solid var(--border)' }}
              onClick={() => setDecision('Rejected')}
            >
              <XCircle size={24} color={decision === 'Rejected' ? '#fff' : 'var(--danger)'} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Reject</span>
            </button>
          </div>
        </div>
        <div className="modal-footer" style={{ border: 'none', paddingTop: 0 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            style={{ flex: 2 }}
            disabled={!decision || !comment || isReviewing} 
            onClick={() => onReview({ status: decision, comments: comment })}
          >
            {isReviewing ? 'Processing...' : 'Submit Decision'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ReviewCenter({ toast }) {
  const qc = useQueryClient()
  const [reviewing, setReviewing] = useState(null)
  const [previewing, setPreviewing] = useState(null)
  
  const { data: pending = [], isLoading } = useQuery({ 
    queryKey: ['reviews', 'pending'], 
    queryFn: api.getPendingReviews 
  })

  const reviewMut = useMutation({
    mutationFn: ({ id, data }) => api.reviewDocument(id, data),
    onSuccess: (data) => { 
      qc.invalidateQueries(['reviews', 'pending'])
      qc.invalidateQueries(['documents'])
      setReviewing(null)

      if (data && data.emailStatus === 'failed') {
        toast('Decision submitted, but failed to notify the submitter via email.', 'warning')
      } else {
        toast('Decision submitted successfully', 'success') 
      }
    },
    onError: e => toast(e.message, 'error'),
  })

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 10, borderRadius: 12 }}>
            <ShieldCheck size={24} />
          </div>
          <div>
            <h1>Review Center</h1>
            <p>Approve, reject, or request improvements for assigned compliance documents.</p>
          </div>
        </div>
      </div>

      <div className="card shadow-sm" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '0.875rem', fontWeight: 700, margin: 0 }}>Pending Tasks ({pending.length})</h2>
        </div>

        <div className="table-wrapper" style={{ margin: 0, border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Document</th>
                <th>Category</th>
                <th>Version</th>
                <th>Submitted By</th>
                <th>Submission Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading pending tasks...</td></tr>
              ) : pending.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 60 }}>
                  <div style={{ opacity: 0.3, marginBottom: 12 }}><CheckCircle size={48} /></div>
                  <p style={{ color: 'var(--text-muted)', fontWeight: 500 }}>All caught up! No pending reviews assigned to you.</p>
                </td></tr>
              ) : pending.map(doc => (
                <tr key={doc.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FileText size={16} color="var(--accent)" />
                      <span style={{ fontWeight: 600 }}>{doc.title}</span>
                    </div>
                  </td>
                  <td><span className="badge badge-neutral">{doc.category}</span></td>
                  <td><span className="font-mono" style={{ fontSize: '0.75rem' }}>v{doc.version}</span></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{doc.submitter_name || 'Admin'}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {doc.submitted_at ? format(new Date(doc.submitted_at), 'dd MMM yyyy HH:mm') : 'Recently'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button className="btn btn-secondary btn-sm" style={{ gap: 8, fontWeight: 600 }} onClick={() => setPreviewing(doc)}>
                        <Eye size={16} />
                        View Document
                      </button>
                      <button className="btn btn-primary btn-sm" style={{ fontWeight: 600 }} onClick={() => setReviewing(doc)}>
                        Perform Review
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reviewing && (
        <ReviewActionModal
          doc={reviewing}
          onClose={() => setReviewing(null)}
          onReview={data => reviewMut.mutate({ id: reviewing.id, data })}
          isReviewing={reviewMut.isPending}
        />
      )}

      {previewing && (
        <FilePreviewModal
          doc={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  )
}
